import { styled } from "styled-components";
import { Page as IPage } from "@ludens-reklame/rubics-v3-sdk/page/types";
import {
  PageSortOrder,
  useTagFilteredSearch,
} from "../../hooks/useTagFilteredSearch.js";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { cssFontSize, cssSpacing } from "../../util/styles.js";
import type { BackgroundColor, MobileColumns } from "../../types/theme.js";
import { Card as BaseCard, CardBody } from "../../internal-components/Card.js";
import { useImageProps } from "../../hooks/useImageProps.js";
import { Busy } from "../../internal-components/Busy.js";
import { RichText } from "../../internal-components/RichText.js";
import { Button, ButtonAction } from "../../internal-components/Button.js";
import { PageFilter } from "../../internal-components/page-filter/PageFilter.js";
import { CustomFilterGroup } from "../JobListings/JobListings.js";

export interface Props {
  title?: string;
  limit?: number;
  showMoreText?: string;
  parent?: IPage;
  link?: Link;
  columns?: 3 | 4;
  mobileColumns?: MobileColumns;
  withSearchAndFilter?: boolean;
  filterToggleText?: string;
  filterSearchLabel?: string;
  filterSearchPlaceholder?: string;
  showAllText?: string;
  resultCountText?: string;
  filterGroups?: CustomFilterGroup[];
  requiredTags?: { value?: string }[];
  backgroundColor?: BackgroundColor;
  sortOrder?: PageSortOrder;
}

export const Pages: React.FC<Props> = ({
  title,
  parent,
  limit,
  showMoreText,
  link,
  columns = 3,
  mobileColumns = 1,
  withSearchAndFilter,
  filterToggleText,
  filterSearchLabel,
  filterSearchPlaceholder,
  showAllText,
  resultCountText,
  filterGroups: customFilterGroups = [],
  requiredTags = [],
  backgroundColor,
  sortOrder,
}) => {
  const store = useStore();

  const resolvedRequiredTags = requiredTags
    .map((tag) => tag.value)
    .filter((value): value is string => !!value);

  const {
    results: items,
    unfilteredTotal,
    loading,
    hasFetched,
    hasMore,
    loadMore,
    activeFilters,
    searchQuery,
    onFilterChange,
  } = useTagFilteredSearch<{}>({
    baseQuery: {
      childOf: parent ? [parent._id] : [store.context.page?._id],
      "sort.publishedDate": -1,
    },
    requiredTags: resolvedRequiredTags,
    pageSize: limit || 20,
    sortOrder,
  });

  const resolvedFilterGroups = customFilterGroups
    .filter((g) => g.label && g.tags?.length)
    .map((g) => ({
      label: g.label!,
      key: g.label!,
      options: (g.tags || [])
        .filter((t) => t.value)
        .map((t) => ({ label: t.value!, value: t.value! })),
    }));

  const isFiltered =
    !!searchQuery ||
    Object.values(activeFilters).some((values) => values.length > 0);
  const showEmptyText =
    isFiltered && hasFetched && !loading && items.length === 0;

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <RelatedArticlesConstrainer>
          {title && <h2>{title}</h2>}
          {withSearchAndFilter && resolvedFilterGroups.length > 0 && (
            <PageFilter
              filterGroups={resolvedFilterGroups}
              filterToggleText={filterToggleText || undefined}
              searchLabel={filterSearchLabel || undefined}
              searchPlaceholder={filterSearchPlaceholder || undefined}
              showAllText={showAllText || undefined}
              resultCountText={resultCountText || undefined}
              onFilterChange={onFilterChange}
              resultCount={
                unfilteredTotal !== undefined
                  ? { shown: items.length, total: unfilteredTotal }
                  : undefined
              }
            />
          )}
          <Busy $busy={loading}>
            {showEmptyText ? (
              <EmptyText>Ingen sider passer til filtreringen</EmptyText>
            ) : (
              <Grid $columns={columns} $mobileColumns={mobileColumns}>
                {items.map((item) => (
                  <Item
                    key={item._id}
                    {...item}
                    backgroundColor={backgroundColor}
                  />
                ))}
              </Grid>
            )}

            {(link || hasMore) && (
              <ButtonWrapper>
                {link ? (
                  <ButtonAction link={link} />
                ) : (
                  <Button onClick={loadMore}>
                    {showMoreText || "Vis flere"}
                  </Button>
                )}
              </ButtonWrapper>
            )}
          </Busy>
        </RelatedArticlesConstrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

const Item: React.FC<IPage & { backgroundColor?: BackgroundColor }> = ({
  url,
  title,
  image,
  excerpt,
  backgroundColor,
}) => {
  const imageProps = useImageProps(
    image,
    {
      width: 716,
      height: 400,
      fit: "cover",
    },
    "lazy"
  );

  return (
    <Card href={url} $bg={backgroundColor}>
      {imageProps && <img {...imageProps} />}
      <CardContent>
        <h4>{title}</h4>
        {excerpt && <RichText dangerouslySetInnerHTML={{ __html: excerpt }} />}
      </CardContent>
    </Card>
  );
};

export const RelatedArticlesConstrainer = styled(Constrainer)`
  h2 {
    margin-bottom: ${cssSpacing("m")};
    margin-top: 0;
    color: var(--pages-component-title-color, inherit);
    font-size: ${cssFontSize("large")};
    font-weight: 600;

    @media (min-width: ${mobileBreakPointCSS}) {
      font-size: ${cssFontSize("larger")};
    }
  }
`;

const EmptyText = styled.p`
  margin: 0;
  padding: ${cssSpacing("xs")} 0;
  color: var(--pages-empty-text-color, inherit);
  font-size: ${cssFontSize("small")};
`;

export const Grid = styled.div<{
  $columns: 3 | 4;
  $mobileColumns: MobileColumns;
}>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$mobileColumns}, 1fr);
  gap: ${cssSpacing("xs")};

  @media (min-width: ${mobileBreakPointCSS}) {
    grid-template-columns: repeat(${(props) => props.$columns || 3}, 1fr);
    gap: ${cssSpacing("l", "m")};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => props.theme.spacing.l};
`;

export const CardContent = styled(CardBody)`
  h4 {
    margin: 0 0 ${(props) => props.theme.spacing.xs};
    color: var(--pages-card-title-color, inherit);
    font-size: ${cssFontSize("medium")};
    font-weight: 600;
  }

  p {
    margin: 0;
    color: var(--pages-card-description-color, inherit);
    white-space: pre-wrap;
    font-size: ${cssFontSize("small")};
  }
`;

export const Card = styled(BaseCard).attrs({
  $background: "var(--pages-card-background)",
})`
  transition: all 0.2s ease-in-out;

  &:hover h4 {
    color: var(--pages-card-title-hover-color, var(--swatch1));
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 220px;
    display: block;
  }
`;
